<template>
  <div>
    <div class="store-setting">
      <div class="flex">
        <div class="field">
          <div class="label">
            <label>Live</label>
          </div>
          <div class="form-control icon-control">
            <label class="toggle-button">
              <input
                type="checkbox"
                v-model="shopDetails.status"
                @click="toggleEnableDisable($event)"
              />
              <span class="toggle-slider round"></span>
            </label>

            <div class="tool-tip">
              <Tooltip
                popOverContent="When turned on, your e-commerce store is live and can be accessed using the Public Link."
                icon="i"
                popOverPosition="right"
              />
            </div>
          </div>
        </div>

        <div class="field">
          <div class="label">
            <label>Logo</label>
            <span>Required</span>
            <small v-if="formErrors.logo" class="error-msg"
              >No image uploaded.</small
            >
          </div>
          <div class="form-control logo-upload">
            <div class="logo-section">
              <div class="portrait">
                <img :src="logoImage" />
              </div>
              <label for="logo-upload" class="custom-file-upload">
                Upload
              </label>
              <input id="logo-upload" type="file" @change="uploadLogo" />
            </div>
          </div>
        </div>

        <div class="field">
          <div class="label">
            <label>Banner</label>
            <span>(Optional)</span>
          </div>
          <div class="form-control">
            <div class="banner-container">
              <div class="banner-section">
                <div class="banner-portrait"><img :src="bannerImage" /></div>
                <label for="banner-upload" class="custom-file-upload">
                  Upload
                </label>
                <input id="banner-upload" type="file" @change="uploadBanner" />
              </div>
            </div>
            <p class="upload-info">
              Max file size: 5MB Dimensions: 1600 x 1050 (px)
            </p>
          </div>
        </div>

        <div class="field">
          <div class="label">
            <label>Page Title</label>
            <span>Required</span>
          </div>
          <div class="form-control">
            <div class="form-group">
              <input
                v-model="shopDetails.title"
                type="text"
                placeholder="Enter store title"
              />
              <small v-if="formErrors.storeTitle" class="error-msg"
                >This field is required.</small
              >
            </div>
          </div>
        </div>

        <div class="field">
          <div class="label">
            <label>Page Description</label>
            <span>Required</span>
          </div>
          <div class="form-control">
            <div class="store-descriptions">
              <div class="default-description description">
                <div class="description-title">
                  <div class="option-selector">
                    <input type="radio" name="description" class="form-input" />
                  </div>
                  <div class="option-label">
                    <label>Default</label>
                  </div>
                </div>
                <div class="description-value">
                  <p>
                    “Introducing [Company Name]’s merch store with custom swag
                    and branded items for purchase. Please allow 3-5 business
                    days for ordering processing.”
                  </p>
                </div>
              </div>

              <div class="custom-description description">
                <div class="description-title">
                  <div class="option-selector">
                    <input type="radio" name="description" class="form-input" />
                  </div>
                  <div class="option-label">
                    <label>Custom</label>
                  </div>
                </div>
                <div class="description-value">
                  <textarea
                    v-model="shopDetails.description"
                    type="text"
                    placeholder="Add description*"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="field">
          <div class="label">
            <label>Out of stock</label>
            <span>Required</span>
          </div>
          <div class="form-control icon-control">
            <label class="toggle-button">
              <input v-model="shopDetails.outOfStock" type="checkbox" />
              <span class="toggle-slider round"></span>
            </label>

            <div class="tool-tip">
              <Tooltip
                popOverContent="When turned on, products that are
not in stock will be visible on your
e-commerce Shop page."
                icon="i"
                popOverPosition="right"
              />
            </div>
          </div>
        </div>

        <div class="field">
          <div class="label">
            <label>Payment</label>
            <span>Required</span>
          </div>
          <div class="form-control">
            <div class="form-group">
              <div class="account-status" v-if="stripeInfoExist">
                <div class="connection-status">
                  <span>Account Status</span>
                  <img
                    v-if="stripeErrors.show"
                    src="../../assets/close-x.svg"
                  />
                  <img v-else src="../../assets/green-tick.svg" />
                </div>

                <div class="details-toggle">
                  <span @click="showStripeDetails = !showStripeDetails">{{
                    showStripeDetails ? 'Hide Details' : 'Show Details'
                  }}</span>
                </div>
              </div>

              <transition>
                <div class="stripe-details" v-if="showStripeDetails">
                  <div>
                    <div class="row">
                      <div>Account Type</div>
                      <div>{{ stripeInfo.type }}</div>
                    </div>

                    <div class="row">
                      <div>Account Status</div>
                      <div>
                        {{
                          stripeInfo.verification.disabledReason
                            ? 'Not Connected'
                            : 'Connected'
                        }}
                      </div>
                    </div>

                    <div class="row">
                      <div>Display Name</div>
                      <div>{{ stripeInfo.displayName }}</div>
                    </div>

                    <div class="row">
                      <div>Email</div>
                      <div>{{ stripeInfo.email }}</div>
                    </div>
                  </div>
                  <div>
                    <div class="row">
                      <div>Country</div>
                      <div>{{ stripeInfo.country }}</div>
                    </div>

                    <div class="row">
                      <div>Managed</div>
                      <div>{{ stripeInfo.managed }}</div>
                    </div>

                    <div class="row">
                      <div>Business URL</div>
                      <div>{{ stripeInfo.businessUrl }}</div>
                    </div>

                    <div class="row">
                      <div>Support URL</div>
                      <div>{{ stripeInfo.supportUrl }}</div>
                    </div>
                  </div>
                </div>
              </transition>

              <div class="stripe-errors" v-if="stripeErrors.show">
                <p>
                  <img src="../../assets/icon-35.svg" />
                  <strong>Please update missing information.</strong>
                </p>

                <ul>
                  <li v-for="(error, i) in stripeErrors.errors" :key="i">
                    {{ error.replace('_', ' ') }}
                  </li>
                </ul>
              </div>

              <button
                @click="connectStripe"
                :disabled="stripeButtonLoading"
                class="button-gemnote button-gemnote--small connect-stripe"
              >
                <img
                  v-if="stripeButtonLoading"
                  src="../../assets/loading-white.svg"
                />
                <span v-if="!stripeInfoExist">Connect with </span>
                <span v-else>Update using </span>
                <img src="../../assets/stripe-logo.svg" />
              </button>
            </div>
          </div>
        </div>

        <div class="field" v-if="stripeInfoExist && !stripeErrors.show">
          <div class="label">
            <label>Public link</label>
          </div>
          <div class="form-control icon-control">
            <div class="link-content">
              <span>
                {{ shopDetails.storeLink ? shopDetails.storeLink : '' }}
              </span>
              <div>
                <img src="../../assets/copy-icon.svg" @click="doCopy" />
              </div>
            </div>
          </div>
        </div>

        <div class="cta">
          <div class="btn-save">
            <button
              @click="updateStoreInfo"
              :disabled="
                canCreateLink ||
                  !stripeInfoExist ||
                  stripeErrors.show ||
                  !shopDetails.status ||
                  updatingStore
              "
              class="button-gemnote button-gemnote--medium"
            >
              <img v-if="updatingStore" src="../../assets/loading-white.svg" />
              <span v-if="shopExist">Save</span>
              <span v-else>Create Public Link</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from '@/internatinalization/i18n'
import {
  progressStatus,
  defaultImageUploadPlaceholder,
  status
} from '@/helpers'
import { mapState } from 'vuex'
import Tooltip from '../../components/Universal/ToolTipInfo.vue'

let bodyFormData = new FormData()
export default {
  name: 'StoreSetting',
  components: { Tooltip },
  data() {
    return {
      noImage: defaultImageUploadPlaceholder,
      defaultLogo: '../../assets/default-logo.png',
      updatingStore: false,
      showStripeDetails: false,

      formErrors: {
        logo: false,
        storeTitle: false
      }
    }
  },
  mounted() {
    this.checkStripe()
  },
  methods: {
    async checkStripe() {
      if (this.$route.query.account_id) {
        await this.$store.dispatch('ecommerce/updateStripe', {
          store_slug: this.shopDetails.slug,
          account_id: this.$route.query.account_id
        })
      }
    },
    doCopy() {
      this.$copyText(this.shopDetails.storeLink).then(
        () => {
          this.$store.dispatch('notification/pushNotification', {
            type: 'success',
            messages: [`Store link copied`]
          })
        },
        () => {
          this.$store.dispatch('notification/pushNotification', {
            type: 'error',
            messages: [`Can not copy store link.`]
          })
        }
      )
    },
    uploadLogo(e) {
      if (e.target.files.length > 0) {
        let size = e.target.files[0].size
        size = Math.round(size / 1024)

        if (size >= 2048) {
          this.$store.dispatch('notification/pushNotification', {
            type: 'error',
            messages: [i18n.t('validations.image-size-exceeded')]
          })
          return
        }
        bodyFormData.delete('e_commerce_store[logo_file]')
        bodyFormData.append('e_commerce_store[logo_file]', e.target.files[0])
        this.shopDetails.logoImage = {
          url: URL.createObjectURL(e.target.files[0])
        }
      }
    },
    uploadBanner(e) {
      if (e.target.files.length > 0) {
        let size = e.target.files[0].size
        size = Math.round(size / 1024)

        if (size >= 2048) {
          this.$store.dispatch('notification/pushNotification', {
            type: 'error',
            messages: [i18n.t('validations.image-size-exceeded')]
          })
          return
        }

        bodyFormData.delete('e_commerce_store[banner_file]')
        bodyFormData.append('e_commerce_store[banner_file]', e.target.files[0])
        if (this.shopDetails.bannerImage !== null) {
          this.shopDetails.bannerImage.url = URL.createObjectURL(
            e.target.files[0]
          )
        } else {
          this.shopDetails.bannerImage = {
            url: URL.createObjectURL(e.target.files[0])
          }
        }
      }
    },
    async updateStoreInfo() {
      this.updatingStore = true
      let newStoreLogo = bodyFormData.get('e_commerce_store[logo_file]')
      if (newStoreLogo === null) {
        bodyFormData.delete('e_commerce_store[logo_file]')
      }

      let newBanner = bodyFormData.get('e_commerce_store[banner_file]')
      if (newBanner === null) {
        bodyFormData.delete('e_commerce_store[banner_file]')
      }

      bodyFormData.set('e_commerce_store[title]', this.shopDetails.title)
      bodyFormData.set(
        'e_commerce_store[description]',
        this.shopDetails.description
      )
      bodyFormData.set(
        'e_commerce_store[primary_color]',
        this.shopDetails.primaryColor
      )

      bodyFormData.set(
        'e_commerce_store[out_of_stock]',
        this.shopDetails.outOfStock
      )

      await this.$store.dispatch('ecommerce/editStore', bodyFormData)
      this.updatingStore = false
    },
    toggleEnableDisable() {
      if (this.shopExist) {
        this.$store.dispatch('ecommerce/enableDisableStore')
      }
    },

    async connectStripe() {
      if (this.shopExist) {
        await this.updateStoreInfo()
        if (this.updatingShop !== status.ERROR) {
          await this.$store.dispatch('ecommerce/connectStripe')
        }
      } else {
        await this.createStore()
        if (this.creatingStore !== status.ERROR) {
          await this.$store.dispatch('ecommerce/connectStripe')
        }
      }
    },

    formIsValid() {
      let formIsValid = true
      let storeLogo = bodyFormData.get('e_commerce_store[logo_file]')

      let storeTitle = this.shopDetails.title

      if (storeLogo) {
        this.formErrors.logo = false
      } else {
        formIsValid = false
        this.formErrors.logo = true
      }

      if (storeTitle) {
        this.formErrors.storeTitle = false
      } else {
        formIsValid = false
        this.formErrors.storeTitle = true
      }

      return formIsValid
    },

    async createStore() {
      if (!this.formIsValid()) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'error',
          messages: [i18n.t('ecommerce.action.create-store-validation-error')]
        })
        return
      }

      bodyFormData.set('e_commerce_store[paige_team_id]', this.team.id)
      bodyFormData.set('e_commerce_store[title]', this.shopDetails.title)
      bodyFormData.set(
        'e_commerce_store[description]',
        this.shopDetails.description
      )
      bodyFormData.set('e_commerce_store[primary_color]', '#000000')
      bodyFormData.set('e_commerce_store[secondary_color]', '#000000')
      bodyFormData.set(
        'e_commerce_store[out_of_stock]',
        this.shopDetails.outOfStock ? true : false
      )

      await this.$store.dispatch('ecommerce/createStore', bodyFormData)
    }
  },
  computed: {
    loadStatus() {
      return progressStatus(this.loadShopDetails)
    },
    stripeErrors() {
      if (this.shopDetails.stripeAccountBlob) {
        if (this.shopDetails.stripeAccountBlob.verification.disabledReason) {
          return {
            show: true,
            errors: this.shopDetails.stripeAccountBlob.verification.fieldsNeeded
              .length
              ? this.shopDetails.stripeAccountBlob.verification.fieldsNeeded
              : this.shopDetails.stripeAccountBlob.verification.errors
          }
        }
      }
      return {
        show: false,
        errors: []
      }
    },
    stripeInfoExist() {
      return !!this.shopDetails.stripeAccountBlob
    },
    canCreateLink() {
      if (this.shopDetails.stripeAccountBlob) {
        return !!this.shopDetails.stripeAccountBlob.verification.disabledReason
      }
      return false
    },
    logoImage() {
      return this.shopDetails.logoImage.url
        ? this.shopDetails.logoImage.url
        : require('../../assets/default-logo.png')
    },
    bannerImage() {
      return this.shopDetails.bannerImage.url
        ? this.shopDetails.bannerImage.url
        : require('../../assets/default-banner.png')
    },
    stripeButtonLoading() {
      return this.connectingStripe || this.creatingStore
    },
    showConnectStripe() {
      if (this.shopExist) {
        return true
      } else {
        return true
      }
    },
    stripeInfo() {
      return this.shopDetails.stripeAccountBlob
    },
    shopExist() {
      return !!this.shopDetails.id
    },
    ...mapState({
      loadShopDetails: state => state.ecommerce.loadShopDetails,
      shopDetails: state => state.ecommerce.shopDetails,
      updatingShop: state => state.ecommerce.updatingShop,
      creatingStore: state => state.ecommerce.creatingStore,
      connectingStripe: state => state.ecommerce.connectingStripe,
      team: state => state.team.team
    })
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.store-setting {
  margin-top: 4rem;
  text-align: left;
  background: $main-white;
  border: 1px solid $main-6;
  padding: 30px 50px;
  position: relative;
  z-index: 50000;
}

.field {
  display: flex;
  flex-direction: row;
  margin: 25px 0;

  .icon-control {
    display: flex;
    gap: 1.5rem;
  }

  .logo-upload {
    img {
      height: 65px;
    }
  }

  .banner-portrait {
    text-align: center;
    background: white;
    padding: 0px;
    border: 1px solid #979797;
    display: flex;
    img {
      width: 240px;
      height: auto;
    }
  }

  .label {
    width: 15%;
    text-align: left !important;
    display: flex;
    flex-direction: column;
    span {
      color: rgba(37, 40, 42, 0.5);
      font-size: 12px;
      font-style: italic;
      font-weight: 100;
    }
    label {
      display: inline !important;
      text-align: left;
      font-size: 14px;
    }
  }

  .form-control {
    width: 85%;
    text-align: left !important;

    input[type='text'],
    textarea {
      width: 220px;
      font-size: 16px;
      color: #25282a;
      border: none;
      padding: 13px;
      border-radius: 0px;
      outline: none;
      -webkit-box-shadow: none;
      box-shadow: none;
      border-bottom: 1px solid #25282a;
      font-family: 'Nunito Sans', sans-serif;
      background: transparent;
    }
  }
}

.logo-section,
.banner-section {
  display: flex;
  align-items: end;
  gap: 1.5rem;
}

.custom-file-upload {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 19px;
  text-decoration-line: underline;
  color: #24292c;
}

.upload-info {
  font-size: 10px;
  line-height: 14px;
  color: rgba(36, 41, 44, 0.5);
}

input[type='text'] {
  width: 300px;
}

.store-descriptions {
  .description {
    display: flex;
    flex-direction: row;
    gap: 2em;
    align-items: flex-start;

    .description-title {
      .option-selector {
        display: flex;

        input {
          height: 16px;
          width: 16px;
        }
      }
    }
  }

  .custom-description {
    margin-top: 30px;
  }

  .description-title {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
  }
}
.cta {
  text-align: right;
  margin-top: 70px;

  .button-gemnote--medium {
    width: 10rem !important;
    display: flex;
    margin-left: auto;
    gap: 20px;
  }
}

.connect-stripe {
  width: 12rem;
  display: flex;
  border-radius: 4px;
  padding: 10px 20px;
  background-color: #635bfe;
  border: #635bfe;
  font-weight: lighter;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover,
  &:focus {
    background-color: #635bfe;
    border: #635bfe;
  }
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 20px;
}

.account-status {
  margin-bottom: 30px;
  display: flex;
  gap: 20px;

  .details-toggle {
    span {
      text-decoration: underline;
      font-size: 14px;
      font-weight: 100;
      cursor: pointer;
    }
  }
}

.stripe-details {
  display: flex;
  border: 0.5px solid #979797;
  width: 550px;
  margin: 20px 0;
  & > div {
    width: 50%;
    padding: 20px;

    .row {
      display: flex;
      gap: 10px;
      padding: 5px 0;

      & > div:first-child {
        width: 50%;
        font-weight: 700;
      }

      & > div {
        width: 50%;
        font-size: 12px;
        color: #7b7a7a;
        font-weight: 400;
        text-align: left;
        line-break: anywhere;
      }
    }
  }
}

.error-msg {
  color: #da5737;
  font-size: 10px;
  line-height: 14px;
  display: block;
}

.button-gemnote:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.stripe-errors {
  p {
    display: flex;
    gap: 10px;
  }
  ul {
    margin-left: 20px;
    margin-bottom: 40px;
    margin-top: 20px;
    text-transform: capitalize;

    li {
      font-size: 14px;
      color: #da5737;
      padding: 5px;
      margin: 2px;
      line-height: normal;
    }
  }
}

.link-content {
  display: flex;
  justify-content: flex-start;
  width: 100%;

  span {
    border: 1px solid $onyx;
    text-align: justify;
    padding: 0.5rem 0.5rem;
  }

  div {
    min-width: 50px;
    border-top: 1px solid $onyx;
    border-bottom: 1px solid $onyx;
    border-right: 1px solid $onyx;
    display: flex;
    justify-content: center;

    img {
      cursor: pointer;
      width: 1.2rem;
    }
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
